import React, { useRef, useState } from 'react';
import { httpClient } from '@/infrastructure/http/client/httpClient';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}

type WrapKind =
  | 'bold'
  | 'italic'
  | 'h2'
  | 'ul'
  | 'ol'
  | 'link'
  | 'code';

function wrapSelection(
  value: string,
  start: number,
  end: number,
  kind: WrapKind
): { next: string; selectionStart: number; selectionEnd: number } {
  const selected = value.slice(start, end) || (kind === 'link' ? 'texto' : 'texto');
  let inserted = selected;
  let cursorOffsetStart = 0;
  let cursorOffsetEnd = 0;

  switch (kind) {
    case 'bold':
      inserted = `**${selected}**`;
      cursorOffsetStart = 2;
      cursorOffsetEnd = 2 + selected.length;
      break;
    case 'italic':
      inserted = `*${selected}*`;
      cursorOffsetStart = 1;
      cursorOffsetEnd = 1 + selected.length;
      break;
    case 'h2':
      inserted = `\n## ${selected}\n`;
      cursorOffsetStart = 4;
      cursorOffsetEnd = 4 + selected.length;
      break;
    case 'ul':
      inserted = selected
        .split('\n')
        .map((line) => (line.trim() ? `- ${line.replace(/^[-*]\s+/, '')}` : '- '))
        .join('\n');
      cursorOffsetStart = 0;
      cursorOffsetEnd = inserted.length;
      break;
    case 'ol':
      inserted = selected
        .split('\n')
        .map((line, i) =>
          line.trim() ? `${i + 1}. ${line.replace(/^\d+\.\s+/, '')}` : `${i + 1}. `
        )
        .join('\n');
      cursorOffsetStart = 0;
      cursorOffsetEnd = inserted.length;
      break;
    case 'link':
      inserted = `[${selected}](https://)`;
      cursorOffsetStart = selected.length + 3;
      cursorOffsetEnd = selected.length + 11;
      break;
    case 'code':
      inserted = `\`${selected}\``;
      cursorOffsetStart = 1;
      cursorOffsetEnd = 1 + selected.length;
      break;
  }

  const next = value.slice(0, start) + inserted + value.slice(end);
  return {
    next,
    selectionStart: start + cursorOffsetStart,
    selectionEnd: start + cursorOffsetEnd,
  };
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const applyWrap = (kind: WrapKind) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const result = wrapSelection(value, start, end, kind);
    onChange(result.next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  const insertAtCursor = (snippet: string) => {
    const el = textareaRef.current;
    if (!el) {
      onChange(value + snippet);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + snippet + value.slice(end);
    onChange(next);
    const caret = start + snippet.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const insertImageByUrl = () => {
    const url = window.prompt('URL de la imagen', 'https://');
    if (!url?.trim()) return;
    const alt = window.prompt('Texto alternativo', 'imagen') || 'imagen';
    insertAtCursor(`\n![${alt}](${url.trim()})\n`);
  };

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await httpClient.post<{ url: string }>('/help/media', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [
          (data, headers) => {
            // Dejar que el browser setee boundary en FormData
            if (data instanceof FormData && headers) {
              delete (headers as Record<string, unknown>)['Content-Type'];
            }
            return data;
          },
        ],
        timeout: 60000,
      });
      const url = response.data.url;
      const alt = file.name.replace(/\.[^.]+$/, '') || 'imagen';
      insertAtCursor(`\n![${alt}](${url})\n`);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'No se pudo subir la imagen');
      setUploadError(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const ToolBtn: React.FC<{
    label: string;
    title: string;
    onClick: () => void;
    disabled?: boolean;
  }> = ({ label, title, onClick, disabled }) => (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
    >
      {label}
    </button>
  );

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        <ToolBtn label="B" title="Negrita" onClick={() => applyWrap('bold')} />
        <ToolBtn label="I" title="Cursiva" onClick={() => applyWrap('italic')} />
        <ToolBtn label="H2" title="Título" onClick={() => applyWrap('h2')} />
        <ToolBtn label="• Lista" title="Lista" onClick={() => applyWrap('ul')} />
        <ToolBtn label="1. Lista" title="Lista numerada" onClick={() => applyWrap('ol')} />
        <ToolBtn label="Link" title="Enlace" onClick={() => applyWrap('link')} />
        <ToolBtn label="<>" title="Código" onClick={() => applyWrap('code')} />
        <ToolBtn
          label={uploading ? 'Subiendo…' : '🖼 Subir'}
          title="Subir imagen"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        />
        <ToolBtn label="URL img" title="Insertar imagen por URL" onClick={insertImageByUrl} />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={(e) => void handleFileSelected(e.target.files?.[0])}
      />

      {uploadError && (
        <p className="mb-2 text-xs text-red-600 dark:text-red-400">{uploadError}</p>
      )}

      <textarea
        ref={textareaRef}
        className="flex-1 min-h-[24rem] w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-3 text-sm font-mono text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        placeholder={placeholder}
      />
    </div>
  );
};
